import { ReactNode, useCallback, useMemo, useState } from "react";

import { AllValueType } from "@bridgesplit/utils";
import { DispatchType } from "@bridgesplit/react";

import { TextButton } from "../../buttons";
import { Column, Row } from "../../containers";
import { EmptyPlaceholder, EmptyPlaceholderProps, Pagination } from "../../misc";
import { Text } from "../../typography";
import { SelectionContext } from "../contexts";
import { SelectionProp } from "../tableProps";
import { useSelection } from "../useSelection";
import { TABLE_CTA_COLUMN_KEY, TableColumn, TableKey, TableRow } from "../types";
import { findCardColumn } from "./cardCommon";
import { FilterCarousel } from "./FilterCarousel";
import { MobileSelectBar } from "./MobileSelectBar";
import { PositionCard } from "./PositionCard";

export { ChipValue } from "./FilterCarousel";

export interface MobilePortfolioRowConfig {
    headerDataIndex: string;
    primaryValueDataIndex: string;
    statDataIndexes: string[];
    actionDataIndex?: string;
}

export interface MobilePortfolioSelection<TPosition> {
    selected: Set<TableKey>;
    setSelected: DispatchType<Set<TableKey>>;
    getKey: (position: TPosition) => TableKey;
    bulkActionNode?: ReactNode;
}

export interface MobilePortfolioPagination {
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
    totalCount: number | undefined;
}

export interface MobilePortfolioCardsProps<TGroup, TPosition> {
    filterLabel: string;
    activeFilter: AllValueType | TableKey;
    onFilterChange: (next: AllValueType | TableKey) => void;

    groups: TableRow<TGroup>[] | undefined;
    renderChip: (row: TableRow<TGroup> | undefined, index: number) => ReactNode;
    groupsLoading?: boolean;

    positions: TableRow<TPosition>[] | undefined;
    positionColumns: TableColumn<TPosition>[];
    rowConfig: MobilePortfolioRowConfig;
    onPositionNavigatePath: (position: TPosition) => string | undefined;
    positionsLoading?: boolean;
    pagination: MobilePortfolioPagination;

    hasHealth: boolean;
    hasSelect: boolean;
    selection?: MobilePortfolioSelection<TPosition>;

    stickyTop?: number | string;
    emptyPlaceholder?: EmptyPlaceholderProps | string;
    skeletonRowCount?: number;
    listLabel?: ReactNode;
    allCard: ReactNode;
}

export function MobilePortfolioCards<TGroup, TPosition>({
    filterLabel,
    activeFilter,
    onFilterChange,
    groups,
    renderChip,
    groupsLoading,
    positions,
    positionColumns,
    rowConfig,
    onPositionNavigatePath,
    positionsLoading,
    pagination,
    hasHealth,
    hasSelect,
    selection,
    stickyTop,
    emptyPlaceholder = "No data",
    skeletonRowCount,
    listLabel,
    allCard
}: MobilePortfolioCardsProps<TGroup, TPosition>) {
    const [selecting, setSelecting] = useState(false);

    const headerColumn = findCardColumn(positionColumns, rowConfig.headerDataIndex);
    const primaryColumn = findCardColumn(positionColumns, rowConfig.primaryValueDataIndex);
    const statColumns = rowConfig.statDataIndexes
        .map((id) => findCardColumn(positionColumns, id))
        .filter((c): c is TableColumn<TPosition> => !!c);
    const actionColumn = findCardColumn(positionColumns, rowConfig.actionDataIndex ?? TABLE_CTA_COLUMN_KEY);

    const getItemsForRow = useCallback((row: TableRow<TPosition>) => [row.data], []);
    const getKey = useCallback((item: TPosition) => selection?.getKey(item) ?? "", [selection]);
    const selectionProp = useMemo<SelectionProp<TPosition> | undefined>(() => {
        if (!selection) return undefined;
        return { selected: selection.selected, setSelected: selection.setSelected, getRowKey: selection.getKey };
    }, [selection]);

    const selectionEnabled = hasSelect && selecting && !!selectionProp;
    const sel = useSelection<TPosition, TPosition>({
        isGrouped: false,
        selection: selectionEnabled ? selectionProp : undefined,
        visibleRows: positions,
        getItemsForRow,
        getKey
    });

    const { pageSize, totalCount, page, setPage } = pagination;

    const placeholder =
        typeof emptyPlaceholder === "string" ? (
            <EmptyPlaceholder header={emptyPlaceholder} />
        ) : (
            <EmptyPlaceholder {...emptyPlaceholder} />
        );

    const positionCards = (
        <Column spacing={1.5}>
            {positions?.map((row, i) => (
                <PositionCard<TPosition>
                    key={row.key}
                    row={row}
                    index={i}
                    headerColumn={headerColumn}
                    primaryColumn={primaryColumn}
                    statColumns={statColumns}
                    actionColumn={actionColumn}
                    navigatePath={onPositionNavigatePath(row.data)}
                    selecting={selectionEnabled}
                />
            ))}
        </Column>
    );

    const positionSkeletons = (
        <Column spacing={1.5}>
            {Array.from({ length: skeletonRowCount ?? pageSize }).map((_, i) => (
                <PositionCard<TPosition>
                    key={i}
                    loading
                    headerColumn={headerColumn}
                    primaryColumn={primaryColumn}
                    statColumns={statColumns}
                    actionColumn={actionColumn}
                />
            ))}
        </Column>
    );

    const bodyNode = (() => {
        if (positionsLoading || positions === undefined) return positionSkeletons;
        if (!positions.length) return placeholder;
        if (!sel.selectionContextValue) return positionCards;
        return <SelectionContext.Provider value={sel.selectionContextValue}>{positionCards}</SelectionContext.Provider>;
    })();

    const showListHeader = listLabel !== undefined || (hasSelect && !!selectionProp);

    return (
        <Column spacing={2.5}>
            {selectionEnabled && (
                <MobileSelectBar
                    onClear={() => selection?.setSelected(new Set())}
                    bulkActionNode={selection?.bulkActionNode}
                    stickyTop={stickyTop}
                />
            )}

            <Column spacing={1.5}>
                <Text variant="body1" sx={{ fontWeight: 500 }}>
                    {filterLabel}
                </Text>
                <FilterCarousel<TGroup>
                    groups={groups ?? []}
                    activeFilter={activeFilter}
                    onFilterChange={onFilterChange}
                    minWidth={hasHealth ? 176 : 148}
                    renderChip={renderChip}
                    allCard={allCard}
                    loading={groupsLoading}
                />
            </Column>

            <Column spacing={1.5}>
                {showListHeader && (
                    <Row spaceBetween sx={{ alignItems: "center" }}>
                        <Row sx={{ alignItems: "baseline", gap: 1 }}>
                            {listLabel !== undefined && (
                                <Text variant="body1" sx={{ fontWeight: 500 }}>
                                    {listLabel}
                                </Text>
                            )}
                            {totalCount !== undefined && (
                                <Text variant="body1" color="caption">
                                    {totalCount}
                                </Text>
                            )}
                        </Row>
                        {hasSelect && !!selectionProp && (
                            <TextButton color="primary" variant="body1" onClick={() => setSelecting((prev) => !prev)}>
                                {selecting ? "Done" : "Select"}
                            </TextButton>
                        )}
                    </Row>
                )}
                {bodyNode}
            </Column>

            {!!totalCount && totalCount > pageSize && (
                <Pagination count={totalCount} rowsPerPage={pageSize} page={page} setPage={setPage} />
            )}
        </Column>
    );
}
